import { useState } from "react";
import { motion } from "framer-motion";
import { Building2, Send, CheckCircle2, Loader2, MessageSquare, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import apiClient, { getApiErrorMessage } from "@/lib/api";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await apiClient.post("/contact", formData);
      if (response.data.success) {
        toast.success("Demande envoyée. Nous vous répondrons sous 24h.", {
          duration: 5000,
          style: {
            background: "#10b981", 
            color: "#ffffff",
            border: "none",
          }
        });
        setFormData({ name: "", email: "", message: "" });
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erreur lors de l'envoi de la demande."), {
        duration: 5000,
        style: {
          background: "#ef4444",
          color: "#ffffff",
          border: "none",
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="section-spacing section-spacing-contact relative overflow-hidden">
      {/* Background - LIGHTER */}
      <div className="absolute inset-0 bg-[#070B1A]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#03050A] via-[#0C1225]/40 to-[#070B1A]" />
      
      {/* Ambient glow */}
      <div className="absolute top-1/3 left-1/4 w-[min(500px,120vw)] h-[400px] rounded-full bg-gradient-to-r from-lenvia-blue/8 to-lenvia-violet/5 blur-3xl" />
      
      <div id="contact" className="lenvia-container relative z-10">
        <div className="grid md:grid-cols-2 gap-14 lg:gap-24 items-start">
          {/* Left - Info */}
          <motion.div
            initial={{ opacity: 0, x: -35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2.5 mb-6">
              <MessageSquare className="w-4 h-4 text-lenvia-blue" />
              <span className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase">
                Contact
              </span>
            </span>
            
            <h2 
              className="font-heading text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight text-white mb-7 leading-[1.08]" 
              data-testid="contact-title"
            >
              Parlons de vos
              <br />
              <span className="gradient-text">enjeux techniques</span>
            </h2>
            
            <p className="text-lg lg:text-xl text-slate-300 mb-4 leading-relaxed" data-testid="contact-description">
              Échangez avec un expert technique (pas un commercial).
            </p>
            <p className="text-base text-slate-400 mb-12 leading-relaxed">
              Nous analysons votre contexte et vos contraintes : performance, architecture, delivery, sécurité.
            </p>

            {/* Trust Badges */}
            <div className="flex flex-col gap-5 mb-12">
              {[
                "Réponse sous 24h",
                "Échange confidentiel",
                "Sans engagement",
                "Premier échange sous NDA si nécessaire"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4 group">
                  <div className="w-8 h-8 rounded-lg bg-lenvia-success/12 border border-lenvia-success/25 flex items-center justify-center group-hover:bg-lenvia-success/18 transition-colors">
                    <CheckCircle2 className="w-4 h-4 text-lenvia-success" />
                  </div>
                  <span className="text-slate-200 font-medium">{item}</span>
                </div>
              ))}
            </div>

            {/* Human presence - Expert portrait + team */}
            <div className="relative rounded-2xl overflow-hidden mb-10 group border border-white/[0.06] shadow-lg shadow-black/20">
              <div className="relative h-52 lg:h-60">
                <img
                  src="https://images.unsplash.com/photo-1600896997793-b8ed3459a17f?w=700&q=75"
                  alt="Expert technique Lenvia"
                  loading="lazy"
                  className="w-full h-full object-cover object-top scale-105 group-hover:scale-100 transition-transform duration-700"
                />
                {/* Premium dark overlays */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#070B1A] via-[#070B1A]/50 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#070B1A]/30 to-transparent" />
                <div className="absolute inset-0 bg-lenvia-blue/[0.05] mix-blend-overlay" />
              </div>
              {/* Caption overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <p className="text-sm font-semibold text-white/80">Échangez avec nos experts</p>
                <p className="text-xs text-slate-400 mt-0.5">Des ingénieurs seniors, pas des commerciaux</p>
              </div>
            </div>

            {/* Contact Info */}
            <div className="pt-8 border-t border-white/[0.06]">
              <div className="flex items-center gap-5">
                <div className="w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-lenvia-blue" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Un pôle d'experts soutenu par</p>
                  <span className="text-white font-bold text-lg">Le Groupe Proxiad</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form 
              onSubmit={handleSubmit} 
              className="glass-card-premium w-full max-w-[560px] mx-auto p-8 lg:p-10 space-y-7" 
              data-testid="contact-form"
            >
              {/* Form Header */}
              <div className="mb-6 text-center">
                <div className="inline-flex items-center gap-2.5 mb-4 px-4 py-2 rounded-full bg-lenvia-success/12 border border-lenvia-success/25">
                  <Sparkles className="w-4 h-4 text-lenvia-success" />
                  <span className="text-xs font-bold text-lenvia-success tracking-wider uppercase">
                    Échange confidentiel — sans engagement
                  </span>
                </div>
                <h3 className="font-heading text-2xl font-bold text-white leading-tight">
                  Discutons de votre contexte technique
                </h3>
              </div>

              <div>
                <label htmlFor="contact-name" className="text-sm font-semibold text-slate-200 mb-3 block">
                  Nom complet *
                </label>
                <Input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Jean Dupont — Directeur IT"
                  required
                  className="form-input w-full p-4"
                  data-testid="input-name"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="text-sm font-semibold text-slate-200 mb-3 block">
                  Email professionnel *
                </label>
                <Input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="jean.dupont@votre-entreprise.com"
                  required
                  className="form-input w-full p-4"
                  data-testid="input-email"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="text-sm font-semibold text-slate-200 mb-3 block">
                  Comment pouvons-nous vous aider ? *
                </label>
                <Textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Décrivez votre contexte (ex : refonte mobile, projet IA, performance...)"
                  required
                  rows={4}
                  className="form-input resize-none p-4"
                  data-testid="input-message"
                />
              </div>

              <div className="pt-4 flex flex-col items-center gap-5">
                <Button
                  type="submit"
                  className="btn-primary w-full justify-center py-6 text-base"
                  disabled={isSubmitting}
                  data-testid="submit-contact"
                >
                  {isSubmitting ? "Envoi en cours..." : "Échanger avec un expert"}
                  {isSubmitting ? <Loader2 className="w-5 h-5 ml-2 animate-spin" /> : <Send className="w-5 h-5 ml-2" />}
                </Button>
                
                <p className="text-center text-xs text-slate-500 px-2 max-w-md leading-relaxed">
                  En soumettant ce formulaire, vous acceptez que vos données soient traitées conformément à notre{" "}
                  <a href="/confidentialite" className="underline hover:text-white transition-colors">
                    Politique de confidentialité
                  </a>.
                </p>
                
                <p className="text-center text-sm text-slate-400 font-semibold">
                  Réponse sous 24h — Sans engagement
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
